import * as d3 from 'd3';
import { Block, Line, ConnectionPoint, Position } from '../types';
import { STYLES } from '../styles/constants';
import { getBlockDimensions } from './blockUtils';
import { getBpmnSymbol } from '../../data/bpmnSymbols';

export type ConnectionSide = 'top' | 'right' | 'bottom' | 'left';

/** Width of the bracket SVG column in text annotation note blocks. */
export const TEXT_ANNOTATION_BRACKET_WIDTH = 12;

export type ConnectionPointOptions = {
  container?: d3.Selection<HTMLDivElement, unknown, null, undefined>;
};

function parsePx(value: string | null | undefined, fallback: number): number {
  if (value == null || value === '') return fallback;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Live canvas geometry from block container DOM (matches drag/resize visuals). */
export function readBlockCanvasGeometry(
  block: Block,
  container: d3.Selection<HTMLDivElement, unknown, null, undefined>
): { x: number; y: number; width: number; height: number } | null {
  const typeClass =
    block.type === 'start' || block.type === 'end' ? block.type : block.type;
  const wrapper = container.select<HTMLDivElement>(`.${typeClass}-block-container-${block.id}`);
  if (wrapper.empty()) return null;

  const defaultDims = getBlockDimensions(block);
  const x = parsePx(wrapper.style('left'), block.x ?? 0);
  const y = parsePx(wrapper.style('top'), block.y ?? 0);

  const inner = wrapper.select<HTMLDivElement>(`.${typeClass}-block-${block.id}`);
  let width = parsePx(wrapper.style('width'), Number.NaN);
  let height = parsePx(wrapper.style('height'), Number.NaN);
  if (!Number.isFinite(width) && !inner.empty()) {
    width = parsePx(inner.style('width'), defaultDims.width);
  }
  if (!Number.isFinite(height) && !inner.empty()) {
    height = parsePx(inner.style('height'), defaultDims.height);
  }
  if (!Number.isFinite(width)) width = defaultDims.width;
  if (!Number.isFinite(height)) height = defaultDims.height;

  return { x, y, width, height };
}

function resolveBlockCanvasGeometry(
  block: Block,
  options?: ConnectionPointOptions
): { x: number; y: number; width: number; height: number } {
  const defaults = getBlockDimensions(block);
  if (options?.container) {
    const fromDom = readBlockCanvasGeometry(block, options.container);
    if (fromDom) return fromDom;
  }
  return {
    x: block.x || 0,
    y: block.y || 0,
    width: block.width ?? defaults.width,
    height: block.height ?? defaults.height,
  };
}

function textAnnotationBracketHeight(
  block: Block,
  container: d3.Selection<HTMLDivElement, unknown, null, undefined> | undefined,
  fallbackHeight: number
): number {
  if (!container) return fallbackHeight;
  const wrapper = container.select(`.note-block-container-${block.id}`);
  const svg = wrapper.select<SVGSVGElement>('svg');
  if (svg.empty()) return fallbackHeight;
  const h = parsePx(svg.attr('height'), Number.NaN);
  if (Number.isFinite(h) && h > 0) return h;
  return parsePx(svg.style('height'), fallbackHeight);
}

export function isTextAnnotationBlock(block: Block): boolean {
  return block.bpmnSymbol === 'textAnnotation';
}

/** BPMN text annotations associate on the bracket side (left edge only). */
export function getVisibleConnectionSides(block: Block): readonly ConnectionSide[] {
  if (isTextAnnotationBlock(block)) {
    return ['left'];
  }
  return ['top', 'right', 'bottom', 'left'];
}

/** Outer diamond tips for IconMagic gateway glyphs (viewBox 0 0 34 34). */
const BPMN_GATEWAY_VIEWBOX = { width: 34, height: 34 };
const BPMN_GATEWAY_TIPS: Record<ConnectionSide, { x: number; y: number }> = {
  top: { x: 17, y: 0.820312 },
  right: { x: 33.117188, y: 17 },
  bottom: { x: 17, y: 33.117188 },
  left: { x: 0.882812, y: 17 },
};

function isBpmnGatewayBlock(block: Block): boolean {
  if (block.type !== 'decision' || !block.bpmnSymbol) return false;
  return getBpmnSymbol(block.bpmnSymbol)?.category === 'gateways';
}

/** Map viewBox tip → canvas coords (same xMidYMid meet as bpmnShapeRenderer). */
function bpmnGatewayConnectionPoint(
  side: ConnectionSide,
  x: number,
  y: number,
  dimensions: { width: number; height: number },
): ConnectionPoint {
  const { width: vw, height: vh } = BPMN_GATEWAY_VIEWBOX;
  const scale = Math.min(dimensions.width / vw, dimensions.height / vh);
  const renderedW = vw * scale;
  const renderedH = vh * scale;
  const offsetX = (dimensions.width - renderedW) / 2;
  const offsetY = (dimensions.height - renderedH) / 2;
  const tip = BPMN_GATEWAY_TIPS[side];
  const inset = 1.5;

  let px = x + offsetX + tip.x * scale;
  let py = y + offsetY + tip.y * scale;

  switch (side) {
    case 'top':
      py -= inset;
      break;
    case 'right':
      px += inset;
      break;
    case 'bottom':
      py += inset;
      break;
    case 'left':
      px -= inset;
      break;
  }

  return { x: px, y: py, side };
}

function bpmnEventConnectionPoint(
  side: ConnectionSide,
  x: number,
  y: number,
  dimensions: { width: number; height: number },
): ConnectionPoint {
  const cx = x + dimensions.width / 2;
  const cy = y + dimensions.height / 2;
  const radius = Math.min(dimensions.width, dimensions.height) / 2;
  const inset = 2;

  switch (side) {
    case 'top':
      return { x: cx, y: cy - radius - inset, side };
    case 'right':
      return { x: cx + radius + inset, y: cy, side };
    case 'bottom':
      return { x: cx, y: cy + radius + inset, side };
    case 'left':
      return { x: cx - radius - inset, y: cy, side };
  }
}

export function calculateConnectionPoint(
  block: Block,
  side: ConnectionSide,
  options?: ConnectionPointOptions
): ConnectionPoint {
  const { x, y, width, height } = resolveBlockCanvasGeometry(block, options);
  const dimensions = { width, height };

  if (block.type === 'start' || block.type === 'end') {
    if (block.bpmnSymbol) {
      return bpmnEventConnectionPoint(side, x, y, dimensions);
    }

    switch (side) {
      case 'top':
        return { x: x + dimensions.width / 2, y: y - 2, side };
      case 'right':
        return { x: x + dimensions.width + 2, y: y + dimensions.height / 2, side };
      case 'bottom':
        return { x: x + dimensions.width / 2, y: y + dimensions.height + 2, side };
      case 'left':
        return { x: x - 2, y: y + dimensions.height / 2, side };
    }
  }

  if (block.type === 'decision') {
    if (isBpmnGatewayBlock(block)) {
      return bpmnGatewayConnectionPoint(side, x, y, dimensions);
    }

    switch (side) {
      case 'top':
        return { x: x + dimensions.width / 2, y, side };
      case 'right':
        return { x: x + dimensions.width, y: y + dimensions.height / 2, side };
      case 'bottom':
        return { x: x + dimensions.width / 2, y: y + dimensions.height, side };
      case 'left':
        return { x, y: y + dimensions.height / 2, side };
    }
  }

  if (isTextAnnotationBlock(block)) {
    const bracketH = textAnnotationBracketHeight(block, options?.container, dimensions.height);
    const anchorX = x + TEXT_ANNOTATION_BRACKET_WIDTH / 2;
    const anchorY = y + bracketH / 2;
    return { x: anchorX, y: anchorY, side: 'left' };
  }

  switch (side) {
    case 'top':
      return { x: x + dimensions.width / 2, y, side };
    case 'right':
      return { x: x + dimensions.width, y: y + dimensions.height / 2, side };
    case 'bottom':
      return { x: x + dimensions.width / 2, y: y + dimensions.height, side };
    case 'left':
      return { x, y: y + dimensions.height / 2, side };
  }
}

export function calculateElbowPoints(from: ConnectionPoint, to: ConnectionPoint): Position[] {
  const points: Position[] = [from];

  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.sqrt(dx * dx + dy * dy);

  if (distance < 30) {
    points.push(to);
    return points;
  }

  const isHorizontallyAligned = Math.abs(from.y - to.y) < 5;
  const isVerticallyAligned = Math.abs(from.x - to.x) < 5;

  if ((isHorizontallyAligned || isVerticallyAligned) && from.side !== to.side) {
    points.push(to);
    return points;
  }

  if (from.side === to.side) {
    const EXTENSION = 5;
    const OFFSET = 10;

    switch (from.side) {
      case 'right':
        points.push({ x: Math.max(from.x, to.x) + OFFSET, y: from.y });
        points.push({ x: Math.max(from.x, to.x) + OFFSET, y: to.y });
        points.push({ x: to.x + EXTENSION, y: to.y });
        points.push(to);
        return points;
      case 'left':
        points.push({ x: Math.min(from.x, to.x) - OFFSET, y: from.y });
        points.push({ x: Math.min(from.x, to.x) - OFFSET, y: to.y });
        points.push({ x: to.x - EXTENSION, y: to.y });
        points.push(to);
        return points;
      case 'top':
        points.push({ x: from.x, y: Math.min(from.y, to.y) - OFFSET });
        points.push({ x: to.x, y: Math.min(from.y, to.y) - OFFSET });
        points.push({ x: to.x, y: to.y - EXTENSION });
        points.push(to);
        return points;
      case 'bottom':
        points.push({ x: from.x, y: Math.max(from.y, to.y) + OFFSET });
        points.push({ x: to.x, y: Math.max(from.y, to.y) + OFFSET });
        points.push({ x: to.x, y: to.y + EXTENSION });
        points.push(to);
        return points;
    }
  }

  const canUseSingleBend = (
    (from.side === 'top' || from.side === 'bottom') &&
    (to.side === 'left' || to.side === 'right') ||
    (from.side === 'left' || from.side === 'right') &&
    (to.side === 'top' || to.side === 'bottom')
  );

  if (canUseSingleBend) {
    const bendPoint = {
      x: from.side === 'top' || from.side === 'bottom' ? from.x : to.x,
      y: from.side === 'left' || from.side === 'right' ? from.y : to.y,
    };
    points.push(bendPoint);
  } else {
    const ratio = Math.min(0.90, distance / 200);

    switch (from.side) {
      case 'right':
        points.push({ x: from.x + Math.abs(dx) * ratio, y: from.y });
        points.push({ x: from.x + Math.abs(dx) * ratio, y: to.y });
        break;
      case 'left':
        points.push({ x: from.x - Math.abs(dx) * ratio, y: from.y });
        points.push({ x: from.x - Math.abs(dx) * ratio, y: to.y });
        break;
      case 'bottom':
        points.push({ x: from.x, y: from.y + Math.abs(dy) * ratio });
        points.push({ x: to.x, y: from.y + Math.abs(dy) * ratio });
        break;
      case 'top':
        points.push({ x: from.x, y: from.y - Math.abs(dy) * ratio });
        points.push({ x: to.x, y: from.y - Math.abs(dy) * ratio });
        break;
    }
  }

  points.push(to);
  return points;
}

/** Association arrow points toward the text annotation (BPMN directed association). */
export function getAssociationLinePoints(
  fromBlock: Block,
  toBlock: Block,
  line: Pick<Line, 'fromSide' | 'toSide'>,
  options?: ConnectionPointOptions
): ConnectionPoint[] {
  const fromSide = (line.fromSide || 'right') as ConnectionSide;
  const toSide = (line.toSide || 'left') as ConnectionSide;
  const startPoint = calculateConnectionPoint(fromBlock, fromSide, options);
  const endPoint = calculateConnectionPoint(toBlock, toSide, options);

  if (isTextAnnotationBlock(fromBlock) && !isTextAnnotationBlock(toBlock)) {
    return [endPoint, startPoint];
  }
  return [startPoint, endPoint];
}

export function createArrowMarker(svg: d3.Selection<SVGSVGElement, unknown, null, undefined>) {
  svg.select('defs').remove();

  const defs = svg.append('defs');

  defs.append('marker')
    .attr('id', 'arrowhead')
    .attr('markerWidth', '12')
    .attr('markerHeight', '6')
    .attr('refX', '3')
    .attr('refY', '3')
    .attr('orient', 'auto')
    .append('polygon')
    .attr('points', '0 0, 4.5 3, 0 6')
    .attr('fill', STYLES.colors.arrow);
}
